import { capitalize } from "../strings/capitalize";

/**
 * Extracts a user-friendly error message from various API error response formats.
 * The returned message always has the first letter capitalized.
 *
 * Handles:
 * - ApiClientError: `error.response.result.message` (Tactic API client)
 * - ResponseWithStatus: `error.result.error.message` or `error.result.message`
 * - Axios error: `error.response?.data?.message`
 * - Standard Error: `error.message` (unless it's a generic HTTP status like "Bad Request")
 * - Plain string error
 * - Object with message property: `{ message: "..." }`
 *
 * @param error - The error object from an API call or catch block
 * @param fallbackMessage - Message to return if no error message can be extracted
 * @returns The extracted error message or the fallback (first letter capitalized)
 *
 * @example
 * ```ts
 * // In mutation onError:
 * onError: (error) => {
 *   toast.error(getApiErrorMessage(error, "Failed to save"));
 * }
 * ```
 */
function extractFromResult(result: Record<string, unknown>): string | undefined {
  // Backend error format: { error: { code, message } }
  if (result.error && typeof result.error === "object") {
    const errorObj = result.error as Record<string, unknown>;
    if (typeof errorObj.message === "string" && errorObj.message) {
      return errorObj.message;
    }
  }
  // Flat format: { message }
  if (typeof result.message === "string" && result.message) {
    return result.message;
  }
  // Field-level errors: { errors: { field: "message" } }
  if (result.errors && typeof result.errors === "object") {
    const errors = result.errors as Record<string, string>;
    const errorMessages = Object.values(errors).filter(Boolean);
    if (errorMessages.length > 0) {
      return errorMessages.join(", ");
    }
  }
  return undefined;
}

export function getApiErrorMessage(
  error: unknown,
  fallbackMessage = "Something went wrong"
): string {
  if (!error) {
    return fallbackMessage;
  }

  // Handle plain string
  if (typeof error === "string") {
    return capitalize(error);
  }

  // Handle object-like errors
  if (typeof error === "object") {
    const err = error as Record<string, unknown>;

    // ResponseWithStatus passed directly (has result and ok properties)
    if ("ok" in err && err.result && typeof err.result === "object") {
      const msg = extractFromResult(err.result as Record<string, unknown>);
      if (msg) return capitalize(msg);
    }

    // ApiClientError style: error.response.result.message (Tactic API)
    // The response is a ResponseWithStatus object
    if (err.response && typeof err.response === "object") {
      const response = err.response as Record<string, unknown>;

      // Check for result (ResponseWithStatus from Tactic API)
      if (response.result && typeof response.result === "object") {
        const msg = extractFromResult(response.result as Record<string, unknown>);
        if (msg) return capitalize(msg);
      }

      // Axios-style: error.response.data.message
      if (response.data && typeof response.data === "object") {
        const data = response.data as Record<string, unknown>;
        if (typeof data.message === "string" && data.message) {
          return capitalize(data.message);
        }
      }
    }

    // Standard Error with message, but skip generic HTTP status messages
    if (typeof err.message === "string" && err.message) {
      const genericMessages = [
        "Bad Request",
        "Unauthorized",
        "Forbidden",
        "Not Found",
        "Internal Server Error",
        "Service Unavailable"
      ];
      if (!genericMessages.includes(err.message)) {
        return capitalize(err.message);
      }
    }
  }

  return fallbackMessage;
}
